import { SCALE_IMAGE_FACTOR } from '../../../features/card-editor/hooks/use-photo';

export const MAX_DEGREES = 360;

/**
 * Converts a rotation angle from [0, 360] to [-180, 180].
 * For example, getComputedRotation(190) returns -170.
 *
 * @param rotation - Rotation angle in degrees [0, 360]
 * @returns Rotation angle in the range of [-180, 180]
 */
export const getComputedRotation = (rotation: number): number =>
  rotation > MAX_DEGREES / 2 ? rotation - MAX_DEGREES : rotation;

/**
 * Determines the orientation of an image (landscape, portrait, or square) based on its rotated bounding box.
 * Takes into account the current angle of the image.
 *
 * @param image - The fabric.Image object
 * @returns 'landscape', 'portrait', or 'square'
 */
export function getCurrentImageOrientation(image: fabric.Image): 'landscape' | 'portrait' | 'square' {
  if (!image.width || !image.height) return 'square';
  const angleRad = ((image.angle ?? 0) * Math.PI) / 180;
  const cos = Math.abs(Math.cos(angleRad));
  const sin = Math.abs(Math.sin(angleRad));
  // Calculate the bounding box after rotation
  const rotatedWidth = image.width * cos + image.height * sin;
  const rotatedHeight = image.width * sin + image.height * cos;
  if (rotatedWidth > rotatedHeight) return 'landscape';
  if (rotatedHeight > rotatedWidth) return 'portrait';
  return 'square';
}

/**
 * Determines the orientation of an image (landscape, portrait, or square) based on its width and height.
 * Ignores rotation.
 *
 * @param image - The fabric.Image object
 * @returns 'landscape', 'portrait', or 'square'
 */
export function getImageOrientation(image: fabric.Image): 'landscape' | 'portrait' | 'square' {
  if (image.width && image.height) {
    if (image.width > image.height) return 'landscape';
    if (image.height > image.width) return 'portrait';
    return 'square';
  }
  return 'square';
}

const hasOrientationFlipped = (original: string, current: string): boolean => {
  return (original === 'portrait' && current === 'landscape') || (original === 'landscape' && current === 'portrait');
};

/**
 * Ensures that an image object fills its associated photo zone after rotation or orientation change.
 * If the orientation changes (portrait <-> landscape), scales the image to fill the zone.
 * Otherwise, resets the scale to the original value.
 *
 * @param object - The fabric.Object (should be an image)
 * @param photozoneObject - The fabric.Object representing the photo zone
 */
export function ensureImageFillsPhotoZone(object: fabric.Object, photozoneObject: fabric.Object) {
  if (object.type !== 'image') return;
  const image = object as fabric.Image;
  const originalOrientation = image.data?.originalOrientation || getImageOrientation(image);
  const originalScale = image.data?.originalScale != null ? image.data.originalScale : 1;
  const currentOrientation = getCurrentImageOrientation(image);

  // Only check if orientation changed (portrait <-> landscape)
  if (hasOrientationFlipped(originalOrientation, currentOrientation)) {
    const angleRad = ((image.angle ?? 0) * Math.PI) / 180;
    const cos = Math.abs(Math.cos(angleRad));
    const sin = Math.abs(Math.sin(angleRad));
    const rotatedWidth = (image.width ?? 0) * cos + (image.height ?? 0) * sin;
    const rotatedHeight = (image.width ?? 0) * sin + (image.height ?? 0) * cos;
    const realWidth = rotatedWidth * (originalScale ?? 1);
    const realHeight = rotatedHeight * (originalScale ?? 1);

    const zoneWidth = photozoneObject.width ?? 0;
    const zoneHeight = photozoneObject.height ?? 0;
    if (realWidth < zoneWidth || realHeight < zoneHeight) {
      const scaleX = zoneWidth / rotatedWidth;
      const scaleY = zoneHeight / rotatedHeight;
      const neededScale = Math.max(scaleX, scaleY) * SCALE_IMAGE_FACTOR;
      image.set({
        scaleX: neededScale,
        scaleY: neededScale,
      });

      image.setCoords();
      image.canvas?.renderAll();
    } else {
      const resetScale = originalScale * SCALE_IMAGE_FACTOR;
      image.set({
        scaleX: resetScale,
        scaleY: resetScale,
      });
    }
  } else {
    image.set({
      scaleX: originalScale,
      scaleY: originalScale,
    });
  }
}
